import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { I18nextProvider } from 'react-i18next'
import { QueryClientProvider } from '@tanstack/react-query'
import i18n from '@app/i18n'
import { runtimeConfig } from '@app/config/runtime'
import { createAppQueryClient } from '@app/query-client'
import { createAppTheme } from '@app/theme'
import { ThemePreferenceProvider } from '@app/ThemePreferenceProvider'
import { useThemePreference } from '@app/theme-preference-context'
import { AuthSessionProvider } from '@features/auth'
import { BrandLockup } from '@features/auth/components/BrandLockup'
import { hasFirebaseConfig, waitForFirebaseAuthReady } from '@services/firebase'
import { NotificationProvider } from '@shared/ui/notifications'
import './providers.css'

function AppBootSplash() {
  const { resolvedTheme } = useThemePreference()

  return (
    <main className="appBootSplash">
      <section className="appBootSplashCard">
        <BrandLockup placement="boot" tone={resolvedTheme === 'dark' ? 'on-dark' : 'default'} />
        <div className="appBootSplashCopy">
          <h1>{runtimeConfig.appTitle}</h1>
          <p>
            Preparing your learning program...
          </p>
        </div>
        <span aria-label="Preparing your learning program" className="appBootSplashSpinner" role="status" />
      </section>
    </main>
  )
}

function FirebaseBootstrapGate({ children }: PropsWithChildren) {
  const [isReady, setIsReady] = useState(!hasFirebaseConfig())

  useEffect(() => {
    if (!hasFirebaseConfig()) {
      return
    }

    let isMounted = true

    void waitForFirebaseAuthReady().then(() => {
      if (isMounted) {
        setIsReady(true)
      }
    })

    return () => {
      isMounted = false
    }
  }, [])

  if (!isReady) {
    return <AppBootSplash />
  }

  return children
}

function AppRuntimeProviders({ children }: PropsWithChildren) {
  const [queryClient] = useState(createAppQueryClient)
  const { resolvedTheme } = useThemePreference()
  const appTheme = useMemo(() => createAppTheme(resolvedTheme), [resolvedTheme])

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <I18nextProvider i18n={i18n}>
          <NotificationProvider autoHideDuration={3500} maxSnack={4}>
            <QueryClientProvider client={queryClient}>
              <FirebaseBootstrapGate>
                <AuthSessionProvider>{children}</AuthSessionProvider>
              </FirebaseBootstrapGate>
            </QueryClientProvider>
          </NotificationProvider>
      </I18nextProvider>
    </ThemeProvider>
  )
}

export function AppProviders({ children }: PropsWithChildren) {
  return (
    <ThemePreferenceProvider>
      <AppRuntimeProviders>{children}</AppRuntimeProviders>
    </ThemePreferenceProvider>
  )
}


